import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, RotateCcw, X, Sliders } from "lucide-react";

const PropertyFilter = ({
  selectedCategory,
  setSelectedCategory,
  filters,
  setFilters,
  applyFilters,
  resetFilters,
  budgetRange = [20, 80],
  setBudgetRange,
  sizeRange = [500, 2500],
  setSizeRange,
  toggleFilter,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const [backupFilters, setBackupFilters] = useState({});
  const [backupBudgetRange, setBackupBudgetRange] = useState([]);
  const [backupSizeRange, setBackupSizeRange] = useState([]);

  const [expandedSections, setExpandedSections] = useState({
    budget: true,
    constructionStatus: true,
    amenities: true,
    typeOfProperty: true,
    bedrooms: true,
    size: true,
    furnishedStatus: true,
    postedBy: true,
    possessionStatus: true,
  });

  useEffect(() => {
    if (isOpen) {
      setBackupFilters({ ...filters });
      setBackupBudgetRange([...budgetRange]);
      setBackupSizeRange([...sizeRange]);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleApplyFilters = () => {
    applyFilters();
    setIsOpen(false);
  };

  const handleResetFilters = () => {
    resetFilters();
  };

  const handleCancelFilter = () => {
    setFilters({ ...backupFilters });
    setBudgetRange([...backupBudgetRange]);
    setSizeRange([...backupSizeRange]);
    setIsOpen(false);
  };

  const FilterSection = ({ title, isExpanded, onToggle, children }) => (
    <div className="border-b border-gray-200">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-3 sm:py-4 px-4 sm:px-5 hover:bg-gray-50 transition-colors"
      >
        <span className="text-xs sm:text-sm md:text-base font-medium text-gray-900">
          {title}
        </span>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
        ) : (
          <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
        )}
      </button>
      {isExpanded && (
        <div className="px-4 sm:px-5 pb-3 sm:pb-4">{children}</div>
      )}
    </div>
  );

  const FilterButton = ({ label, isSelected, onClick }) => (
    <button
      onClick={onClick}
      className={`px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-2xl text-xs sm:text-sm transition-all ${
        isSelected
          ? "bg-blue-100 text-blue-700 border border-blue-300"
          : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
      }`}
    >
      {isSelected ? (
        <span className="mr-1">✕</span>
      ) : (
        <span className="mr-1">✓</span>
      )}
      {label}
    </button>
  );

  const FilterContent = () => (
    <>
      {/* Header - Sticky */}
      <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-200 sticky top-0 bg-white z-20">
        <h2 className="font-medium text-2xl text-gray-900">Filter</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={handleResetFilters}
            className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-gray-700 hover:text-gray-900 transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Reset</span>
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="lg:hidden p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Category Filter - Sticky below header */}
      <div className="p-4 sm:p-5 border-b border-gray-200 bg-gray-50 sticky top-[76px] z-10">
        <div className="flex flex-wrap gap-2">
          <FilterButton
            label="All Properties"
            isSelected={selectedCategory === "all"}
            onClick={() => setSelectedCategory("all")}
          />
          <FilterButton
            label="Residential"
            isSelected={selectedCategory === "residential"}
            onClick={() => setSelectedCategory("residential")}
          />
          <FilterButton
            label="Land"
            isSelected={selectedCategory === "land"}
            onClick={() => setSelectedCategory("land")}
          />
          <FilterButton
            label="Commercial"
            isSelected={selectedCategory === "commercial"}
            onClick={() => setSelectedCategory("commercial")}
          />
          <FilterButton
            label="Project"
            isSelected={selectedCategory === "project"}
            onClick={() => setSelectedCategory("project")}
          />
        </div>
      </div>

      <div className="overflow-y-auto">
        {/* Budget */}
        <FilterSection
          title="Budget"
          isExpanded={expandedSections.budget}
          onToggle={() => toggleSection("budget")}
        >
          <div className="space-y-3 sm:space-y-4">
            <div className="relative pt-1">
              <input
                type="range"
                min="100"
                max="10000000"
                step="100"
                value={budgetRange[0]}
                onChange={(e) =>
                  setBudgetRange([parseInt(e.target.value), budgetRange[1]])
                }
                className="absolute w-full h-2 bg-blue-200 rounded-2xl appearance-none cursor-pointer"
                style={{
                  zIndex: budgetRange[0] > budgetRange[1] - 10 ? 3 : 1,
                }}
              />
              <input
                type="range"
                min="100"
                max="10000000"
                step="100"
                value={budgetRange[1]}
                onChange={(e) =>
                  setBudgetRange([budgetRange[0], parseInt(e.target.value)])
                }
                className="absolute w-full h-2 bg-transparent rounded-2xl appearance-none cursor-pointer"
                style={{ zIndex: 2 }}
              />
            </div>
            <div className="flex justify-between text-xs sm:text-sm text-gray-600 mt-8 sm:mt-10">
              <span>₹{(budgetRange[0] / 100000).toFixed(2)}L</span>
              <span>
                ₹
                {budgetRange[1] >= 10000000
                  ? "10Cr"
                  : (budgetRange[1] / 100000).toFixed(2) + "L"}
              </span>
            </div>
          </div>
        </FilterSection>

        {/* Size(sqft) */}
        <FilterSection
          title="Size(sqft)"
          isExpanded={expandedSections.size}
          onToggle={() => toggleSection("size")}
        >
          <div className="space-y-3 sm:space-y-4">
            <div className="relative pt-1">
              <input
                type="range"
                min="100"
                max="5000"
                value={sizeRange?.[0] || 500}
                onChange={(e) =>
                  setSizeRange &&
                  setSizeRange([
                    parseInt(e.target.value),
                    sizeRange?.[1] || 2500,
                  ])
                }
                className="absolute w-full h-2 bg-blue-200 rounded-2xl appearance-none cursor-pointer"
                style={{
                  zIndex:
                    (sizeRange?.[0] || 500) > (sizeRange?.[1] || 2500) - 10
                      ? 3
                      : 1,
                }}
              />
              <input
                type="range"
                min="100"
                max="5000"
                value={sizeRange?.[1] || 2500}
                onChange={(e) =>
                  setSizeRange &&
                  setSizeRange([
                    sizeRange?.[0] || 500,
                    parseInt(e.target.value),
                  ])
                }
                className="absolute w-full h-2 bg-transparent rounded-2xl appearance-none cursor-pointer"
                style={{ zIndex: 2 }}
              />
            </div>
            <div className="flex justify-between text-xs sm:text-sm text-gray-600 mt-8 sm:mt-10">
              <span>{sizeRange?.[0] || 500} sqft</span>
              <span>{sizeRange?.[1] || 2500} sqft</span>
            </div>
          </div>
        </FilterSection>

        {/* Availability Status */}
        <FilterSection
          title="Availability Status"
          isExpanded={expandedSections.constructionStatus}
          onToggle={() => toggleSection("constructionStatus")}
        >
          <div className="flex flex-wrap gap-2">
            {["under_construction", "ready_to_move"].map((status) => (
              <FilterButton
                key={status}
                label={status.replace(/_/g, " ").toUpperCase()}
                isSelected={filters?.availabilityStatus === status}
                onClick={() =>
                  setFilters &&
                  setFilters((prev) => ({
                    ...prev,
                    availabilityStatus:
                      prev.availabilityStatus === status ? "" : status,
                  }))
                }
              />
            ))}
          </div>
        </FilterSection>

        {/* Type of Property */}
        <FilterSection
          title="Type of Property"
          isExpanded={expandedSections.typeOfProperty}
          onToggle={() => toggleSection("typeOfProperty")}
        >
          <div className="flex flex-wrap gap-2 rounded-2xl">
            {[
              "Plot",
              "Apartment",
              "Villa",
              "Independent House",
              "Land",
              "Shop",
            ].map((type) => (
              <FilterButton
                key={type}
                label={type}
                isSelected={filters?.propertyTypes?.includes(type) || false}
                onClick={() =>
                  toggleFilter && toggleFilter("propertyTypes", type)
                }
              />
            ))}
          </div>
        </FilterSection>

        {/* No. of Bedrooms */}
        <FilterSection
          title="No. of Bedrooms"
          isExpanded={expandedSections.bedrooms}
          onToggle={() => toggleSection("bedrooms")}
        >
          <div className="flex flex-wrap gap-2">
            {["1BHK / 1RK", "2 BHK", "3 BHK", "4 BHK", "6 BHK"].map(
              (bedroom) => (
                <FilterButton
                  key={bedroom}
                  label={bedroom}
                  isSelected={filters?.bedrooms?.includes(bedroom) || false}
                  onClick={() =>
                    toggleFilter && toggleFilter("bedrooms", bedroom)
                  }
                />
              )
            )}
          </div>
        </FilterSection>

        {/* Furnished Status */}
        <FilterSection
          title="Furnished Status"
          isExpanded={expandedSections.furnishedStatus}
          onToggle={() => toggleSection("furnishedStatus")}
        >
          <div className="flex flex-wrap gap-2">
            {["Furnished", "Semi Furnished", "Unfurnished"].map((status) => (
              <FilterButton
                key={status}
                label={status}
                isSelected={filters?.furnishedStatus?.includes(status) || false}
                onClick={() =>
                  toggleFilter && toggleFilter("furnishedStatus", status)
                }
              />
            ))}
          </div>
        </FilterSection>

        {/* Amenities */}
        <FilterSection
          title="Amenities"
          isExpanded={expandedSections.amenities}
          onToggle={() => toggleSection("amenities")}
        >
          <div className="flex flex-wrap gap-2">
            {["Parking", "Lift", "Park", "Power Backup", "Gym"].map(
              (amenity) => (
                <FilterButton
                  key={amenity}
                  label={amenity}
                  isSelected={filters?.amenities?.includes(amenity) || false}
                  onClick={() =>
                    toggleFilter && toggleFilter("amenities", amenity)
                  }
                />
              )
            )}
          </div>
        </FilterSection>

        {/* Featured Properties */}
        <div className="border-b border-gray-200 px-4 sm:px-5 py-3 sm:py-4">
          <label className="flex items-center justify-between cursor-pointer">
            <span className="font-medium text-base text-gray-900">
              Featured Properties Only
            </span>
            <div className="relative">
              <input
                type="checkbox"
                checked={filters?.isFeatured || false}
                onChange={(e) =>
                  setFilters &&
                  setFilters((prev) => ({
                    ...prev,
                    isFeatured: e.target.checked,
                  }))
                }
                className="sr-only"
              />
              <div
                className={`w-10 h-5 sm:w-11 sm:h-6 rounded-2xl transition-colors ${
                  filters?.isFeatured ? "bg-blue-600" : "bg-gray-300"
                }`}
              >
                <div
                  className={`w-4 h-4 sm:w-5 sm:h-5 bg-white rounded-2xl shadow-md transform transition-transform ${
                    filters?.isFeatured
                      ? "translate-x-5 sm:translate-x-6"
                      : "translate-x-0.5"
                  } mt-0.5`}
                ></div>
              </div>
            </div>
          </label>
        </div>

        {/* Verified Properties */}
        <div className="border-b border-gray-200 px-4 sm:px-5 py-3 sm:py-4">
          <label className="flex items-center justify-between cursor-pointer">
            <span className="font-medium text-base text-gray-900">
              Verified Properties Only
            </span>
            <div className="relative">
              <input
                type="checkbox"
                checked={filters?.isApproved || false}
                onChange={(e) =>
                  setFilters &&
                  setFilters((prev) => ({
                    ...prev,
                    isApproved: e.target.checked,
                  }))
                }
                className="sr-only"
              />
              <div
                className={`w-10 h-5 sm:w-11 sm:h-6 rounded-2xl transition-colors ${
                  filters?.isApproved ? "bg-blue-600" : "bg-gray-300"
                }`}
              >
                <div
                  className={`w-4 h-4 sm:w-5 sm:h-5 bg-white rounded-2xl shadow-md transform transition-transform ${
                    filters?.isApproved
                      ? "translate-x-5 sm:translate-x-6"
                      : "translate-x-0.5"
                  } mt-0.5`}
                ></div>
              </div>
            </div>
          </label>
        </div>

        {/* Properties with photos */}
        <div className="border-b border-gray-200 px-4 sm:px-5 py-3 sm:py-4">
          <label className="flex items-center justify-between cursor-pointer">
            <span className="font-medium text-base text-gray-900">
              Properties with photos
            </span>
            <div className="relative">
              <input
                type="checkbox"
                checked={filters?.withPhotos || false}
                onChange={(e) =>
                  setFilters &&
                  setFilters((prev) => ({
                    ...prev,
                    withPhotos: e.target.checked,
                  }))
                }
                className="sr-only"
              />
              <div
                className={`w-10 h-5 sm:w-11 sm:h-6 rounded-2xl transition-colors ${
                  filters?.withPhotos ? "bg-blue-600" : "bg-gray-300"
                }`}
              >
                <div
                  className={`w-4 h-4 sm:w-5 sm:h-5 bg-white rounded-2xl shadow-md transform transition-transform ${
                    filters?.withPhotos
                      ? "translate-x-5 sm:translate-x-6"
                      : "translate-x-0.5"
                  } mt-0.5`}
                ></div>
              </div>
            </div>
          </label>
        </div>

        {/* Properties with videos */}
        <div className="px-4 sm:px-5 py-3 sm:pb-4">
          <label className="flex items-center justify-between cursor-pointer">
            <span className="font-medium text-base text-gray-900">
              Properties with videos
            </span>
            <div className="relative">
              <input
                type="checkbox"
                checked={filters?.withVideos || false}
                onChange={(e) =>
                  setFilters &&
                  setFilters((prev) => ({
                    ...prev,
                    withVideos: e.target.checked,
                  }))
                }
                className="sr-only"
              />
              <div
                className={`w-10 h-5 sm:w-11 sm:h-6 rounded-2xl transition-colors ${
                  filters?.withVideos ? "bg-blue-600" : "bg-gray-300"
                }`}
              >
                <div
                  className={`w-4 h-4 sm:w-5 sm:h-5 bg-white rounded-2xl shadow-md transform transition-transform ${
                    filters?.withVideos
                      ? "translate-x-5 sm:translate-x-6"
                      : "translate-x-0.5"
                  } mt-0.5`}
                ></div>
              </div>
            </div>
          </label>
        </div>

        {/* Apply button for mobile/tablet */}
        <div className="lg:hidden p-4 border-t border-gray-200 sticky bottom-0 bg-white flex gap-2">
          {/* Reset Button */}
          <button
            onClick={handleResetFilters}
            className="w-full bg-red-500 text-white font-semibold py-3 rounded-lg hover:bg-red-600 transition-colors"
          >
            Reset
          </button>

          {/* Cancel Button */}
          <button
            onClick={handleCancelFilter}
            className="w-full bg-gray-300 text-gray-800 font-semibold py-3 rounded-lg hover:bg-gray-400 transition-colors"
          >
            Cancel
          </button>

          {/* Apply Button */}
          <button
            onClick={handleApplyFilters}
            className="w-full bg-blue-600 text-white font-semibold py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Apply
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="w-full">
      {/* Desktop View - Always Visible */}
      <div className="hidden lg:block bg-white rounded-lg overflow-hidden border border-gray-200 sticky top-0">
        <FilterContent />
      </div>

      {/* Mobile/Tablet Filter Button */}
      <div className="lg:hidden">
        <button
          onClick={() => setIsOpen(true)}
          className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Sliders className="w-5 h-5" />
          <span>Filter Properties</span>
        </button>

        {/* Modal Overlay */}
        {isOpen && (
          <div className="fixed inset-0 z-[9999]">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/50 transition-opacity"
              onClick={() => setIsOpen(false)}
            />

            {/* Slide-in Panel */}
            <div className="absolute inset-0 flex flex-col max-w-full">
              <div className="sm:ml-auto sm:w-1/2 bg-white h-full overflow-hidden flex flex-col">
                <FilterContent />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyFilter;
